
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import IconButton from "@/components/UI/IconButton.vue";
import PanelHeader from "@/components/Reusable/Util/PanelHeader.vue";
import { MediaInputValue, MediaOptions } from "@/types/media";
import WPMediaInput from "@/components/Reusable/CustomInputs/MediaInput/WPMediaInput.vue";
import { documentStore, singularityStore, uiStore } from "@/store/store";
import { findNode } from "@/lib/tree/tree";
import lodash from "lodash";
import { UnsplashImage } from "@/futurelayer/api/unsplash";
import FutureLayerImageBrowser from "@/components/Singularity/FutureLayerImageBrowser/FutureLayerImageBrowser.vue";
import { convertUnsplashImageToMediaInputValue } from "@/futurelayer/images/util";
import { getFutureLayerImageMetaFromNode } from "@/futurelayer/images/meta";
import {
  shouldDownloadImageToMediaLibrary,
  downloadImageToMediaLibrary,
  getMediaInputValueFromMediaUploadResponse
} from "@/futurelayer/images/media-library";
import { getOptimalImageUrl } from "@/futurelayer/images/dimensions";
import SingularityPanelHeader from "@/components/Singularity/PanelHeader/SingularityPanelHeader.vue";

@Component({
  components: {
    Dropdown,
    IconButton,
    PanelHeader,
    WPMediaInput,
    FutureLayerImageBrowser,
    SingularityPanelHeader
  }
})
export default class ImageEditor extends Vue {
  originalMediaValue: MediaInputValue | null = null;
  get imageId() {
    return singularityStore.imageEditorPanelState?.imageId;
  }

  get futureLayerImageData() {
    const node = findNode(documentStore.document.tree, this.imageId);
    if (!node) return null;
    return getFutureLayerImageMetaFromNode(node);
  }

  get singleImageControlData() {
    const futureLayerData = this.futureLayerImageData;
    return futureLayerData?.[0] || null; // Take first control only
  }

  get mediaInputValue() {
    if (!this.imageId) return null;

    const node = findNode(documentStore.document.tree, this.imageId);

    if (!node) return null;

    const result = lodash.get(node.data.properties, PATH_TO_MEDIA_INPUT);

    if (!result) return null;

    return result;
  }

  updateMediaInputValue(value: MediaInputValue) {
    if (!this.imageId) return;

    documentStore.unthrottledPropertyChanged({
      elementId: this.imageId,
      path: PATH_TO_MEDIA_INPUT,
      value: value,
      meta: {
        snapshotLabel: "Image Editor Media Input Update"
      }
    });
  }

  handleImagePreviewed(optimalUrl: string) {
    if (!this.originalMediaValue) {
      this.originalMediaValue = this.mediaInputValue;
    }

    const previewValue: MediaInputValue = {
      type: "external_image",
      url: optimalUrl,
      alt: "",
      caption: "",
      id: -1
    };

    this.updateMediaInputValue(previewValue);
  }

  handleImageRestored() {
    if (this.originalMediaValue) {
      this.updateMediaInputValue(this.originalMediaValue);
      this.originalMediaValue = null;
    }
  }

  async handleImageSelected(image: UnsplashImage) {
    this.originalMediaValue = null;

    if (shouldDownloadImageToMediaLibrary(image)) {
      const optimalUrl = getOptimalImageUrl(
        image,
        this.singleImageControlData?.width
      );
      const result = await downloadImageToMediaLibrary(optimalUrl, image);
      if (result) {
        const mediaValue = getMediaInputValueFromMediaUploadResponse(result);
        this.updateMediaInputValue(mediaValue);
        return;
      }
    }

    const mediaValue = convertUnsplashImageToMediaInputValue(image);
    this.updateMediaInputValue(mediaValue);
  }

  addIsOpen = false;
  openAdd() {
    this.addIsOpen = !this.addIsOpen;
  }

  get mediaInputOptions(): MediaOptions {
    return {
      acceptedFileTypes: ["image"]
    };
  }

  get uiStoreActiveElementId() {
    return uiStore.activeElementId;
  }

  @Watch("uiStoreActiveElementId")
  closePanelIfElementIdChanges() {
    if (this.imageId !== this.uiStoreActiveElementId) {
      singularityStore.setImageEditorPanelState(null);
    }
  }
}

const PATH_TO_MEDIA_INPUT = "content.image.media";
